"use client";

import { c as _c } from "react/compiler-runtime";
import { KeyRound, Mail, Upload } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { EmailOtpSection } from "@/components/auth/login/email-otp-section";
import { LoginWalletDetect } from "@/components/auth/login/login-wallet-detect";
import { type OAuthProvider, OAuthProviderButtons } from "@/components/oauth-provider-buttons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getSignInOptionsHeading } from "@/lib/auth/login-copy";
import { SOCIAL_SIGN_IN_NEW_ACCOUNT_NOTICE } from "@/lib/oauth/social-signin-copy";
import { STORAGE_KEYS } from "@/lib/storage-keys";
export type SignInTab = "sign-in" | "restore";
export type { LastSignInMethod } from "@/lib/auth/last-sign-in-method";
import { type LastSignInMethod, LOGIN_METHOD_CHANGED, readLastSignInMethod } from "@/lib/auth/last-sign-in-method";
export const RESTORE_TAB_TRIGGER_ID = "login-restore-tab";
const LAST_USED_RING = "ring-2 ring-primary/60 ring-offset-2 ring-offset-card";

/** Keep tab switches immediate and reserve the same form area in either view. */
const TAB_PANEL = "mt-4 grid min-h-[14.5rem] content-start gap-3";
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Remembering is a convenience; never let it break sign-in.
  }
}
function tabForMethod(method: LastSignInMethod | null): SignInTab | null {
  if (!method) {
    return null;
  }
  return method === "wallet" || method === "file" || method === "phrase" ? "sign-in" : "restore";
}

/**
 * Where a first-time visitor lands. Anything the user already did wins
 * (last method, then last tab). A passkey user who asked for more options,
 * or someone mid email sign-in, lands on Restore. On a phone there is no
 * desktop wallet to connect and no file picker worth leading with, so
 * Restore is the sensible default there too.
 */
export function resolveDefaultSignInTab(input: {
  lastMethod: LastSignInMethod | null;
  lastTab: SignInTab | null;
  wantsRestore: boolean;
  isMobile: boolean;
  /** A BRC-100 wallet is present (extension, or an in-wallet browser). */
  hasWallet: boolean;
}): SignInTab {
  const fromMethod = tabForMethod(input.lastMethod);
  if (fromMethod) {
    return fromMethod;
  }
  if (input.lastTab) {
    return input.lastTab;
  }
  if (input.wantsRestore) {
    return "restore";
  }
  // A phone inside a BRC-100 wallet's browser (BSV Desktop's mobile app)
  // auto-connects, so the wallet button is the right lead there too.
  if (input.isMobile && !input.hasWallet) {
    return "restore";
  }
  return "sign-in";
}
function LastUsed(t0) {
  const $ = _c(5);
  const {
    active,
    children
  } = t0;
  if (!active) {
    return children;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <TooltipTrigger asChild={true}>{children}</TooltipTrigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TooltipContent>Last used on this device</TooltipContent>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <TooltipProvider><Tooltip>{t1}{t2}</Tooltip></TooltipProvider>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/** Content shown when no local backup exists - sign in options */
export function NoBackupContent(t0) {
  const $ = _c(110);
  const {
    emailAuthPending,
    emailForOtp,
    handleEmailOtpSignIn,
    handleSendEmailOtp,
    handleSocialSignIn,
    handleWalletSignIn,
    importHotkey,
    loading,
    oauthClient,
    onBackToPasskey,
    onError,
    recoveryReady,
    handleRecoveryRetry,
    recoveryPassword,
    setRecoveryPassword,
    cancelRecovery,
    onRestorePhrase,
    otpCode,
    otpSent,
    resetEmailOtp,
    setEmailForOtp,
    setOtpCode,
    setOtpSent,
    setShowEmailSignIn,
    showEmailSignIn,
    signupHref,
    socialProviderLoading,
    socialSignInIsPending,
    walletSignInDisabled
  } = t0;
  const recoveryBusy = socialSignInIsPending || emailAuthPending;
  const t1 = Boolean(onBackToPasskey);
  let t2;
  if ($[0] !== oauthClient || $[1] !== t1) {
    t2 = getSignInOptionsHeading(oauthClient, t1);
    $[0] = oauthClient;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const heading = t2;
  const wantsRestore = Boolean(onBackToPasskey) || showEmailSignIn || recoveryReady;
  const [lastMethod, setLastMethod] = useState(null);
  const [tab, setTab] = useState("sign-in");
  const wantsRestoreAtMount = useRef(wantsRestore);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const storedMethod = readLastSignInMethod();
      const storedTab = readStored(STORAGE_KEYS.LOGIN_LAST_TAB) as SignInTab | null;
      setLastMethod(storedMethod);
      setTab(resolveDefaultSignInTab({
        lastMethod: storedMethod,
        lastTab: storedTab === "sign-in" || storedTab === "restore" ? storedTab : null,
        wantsRestore: wantsRestoreAtMount.current,
        isMobile: window.matchMedia("(max-width: 640px), (pointer: coarse)").matches,
        hasWallet: "CWI" in window
      }));
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== wantsRestore) {
    t5 = () => {
      if (wantsRestore) {
        setTab("restore");
      }
    };
    t6 = [wantsRestore];
    $[5] = wantsRestore;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const refresh = () => setLastMethod(readLastSignInMethod());
      window.addEventListener(LOGIN_METHOD_CHANGED, refresh);
      return () => window.removeEventListener(LOGIN_METHOD_CHANGED, refresh);
    };
    t8 = [];
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t9;
  if ($[10] !== cancelRecovery || $[11] !== onError) {
    t9 = value => {
      onError(null);
      cancelRecovery();
      setTab(value === "restore" ? "restore" : "sign-in");
    };
    $[10] = cancelRecovery;
    $[11] = onError;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const handleTabChange = t9;
  const rememberTab = _temp;
  let t10;
  if ($[13] !== lastMethod) {
    t10 = lastMethod?.startsWith("provider:") === true ? lastMethod.slice(9) as OAuthProvider : null;
    $[13] = lastMethod;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const lastProvider = t10;
  let t11;
  if ($[15] !== heading.title) {
    t11 = <h2 className="font-bold text-xl">{heading.title}</h2>;
    $[15] = heading.title;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = tab === "sign-in" ? "Authenticate without a cloud backup" : "Restore from a cloud backup";
  let t13;
  if ($[17] !== t12) {
    t13 = <p className="text-muted-foreground text-sm">{t12}</p>;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t11 || $[20] !== t13) {
    t14 = <div className="flex flex-col items-center gap-2 text-center">{t11}{t13}</div>;
    $[19] = t11;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <TabsTrigger onClick={() => rememberTab("sign-in")} value="sign-in">Sign in</TabsTrigger>;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <TabsList className="grid w-full grid-cols-2">{t15}<TabsTrigger id={RESTORE_TAB_TRIGGER_ID} onClick={() => rememberTab("restore")} value="restore">Restore</TabsTrigger></TabsList>;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== onBackToPasskey) {
    t17 = onBackToPasskey ? <Button className="w-full" onClick={onBackToPasskey} type="button" variant="outline">Unlock with Passkey</Button> : null;
    $[24] = onBackToPasskey;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const t18 = lastMethod === "wallet";
  const t19 = lastMethod === "wallet" ? `rounded-md ${LAST_USED_RING}` : "";
  let t20;
  if ($[26] !== handleWalletSignIn) {
    t20 = bapId => handleWalletSignIn(bapId);
    $[26] = handleWalletSignIn;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== onError || $[29] !== signupHref || $[30] !== t20 || $[31] !== walletSignInDisabled) {
    t21 = <LoginWalletDetect disabled={walletSignInDisabled} onError={onError} onSignedIn={t20} signupHref={signupHref} />;
    $[28] = onError;
    $[29] = signupHref;
    $[30] = t20;
    $[31] = walletSignInDisabled;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t19 || $[34] !== t21) {
    t22 = <div className={t19}>{t21}</div>;
    $[33] = t19;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t18 || $[37] !== t22) {
    t23 = <LastUsed active={t18}>{t22}</LastUsed>;
    $[36] = t18;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const t24 = lastMethod === "file";
  const t25 = `w-full ${lastMethod === "file" ? LAST_USED_RING : ""}`;
  let t26;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Upload className="mr-2 h-4 w-4" />;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== importHotkey || $[41] !== loading || $[42] !== t25) {
    t27 = <Button className={t25} disabled={loading} onClick={_temp2} size="lg" type="button" variant="outline">{t26}Import backup file{importHotkey}</Button>;
    $[40] = importHotkey;
    $[41] = loading;
    $[42] = t25;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== t24 || $[45] !== t27) {
    t28 = <LastUsed active={t24}>{t27}</LastUsed>;
    $[44] = t24;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] !== lastMethod || $[48] !== loading || $[49] !== onRestorePhrase) {
    t29 = onRestorePhrase && <LastUsed active={lastMethod === "phrase"}><Button className={`w-full ${lastMethod === "phrase" ? LAST_USED_RING : ""}`} disabled={loading} onClick={() => {
        onRestorePhrase();
      }} type="button" variant="outline"><KeyRound className="mr-2 h-4 w-4" />Use recovery phrase</Button></LastUsed>;
    $[47] = lastMethod;
    $[48] = loading;
    $[49] = onRestorePhrase;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== t17 || $[52] !== t23 || $[53] !== t28 || $[54] !== t29) {
    t30 = <TabsContent className={TAB_PANEL} value="sign-in">{t17}{t23}{t28}{t29}</TabsContent>;
    $[51] = t17;
    $[52] = t23;
    $[53] = t28;
    $[54] = t29;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  if ($[56] !== handleRecoveryRetry || $[57] !== onError || $[58] !== recoveryBusy || $[59] !== recoveryPassword || $[60] !== recoveryReady || $[61] !== setRecoveryPassword) {
    t31 = recoveryReady && <><div className="grid gap-2"><Label htmlFor="restore-decryption-password">Backup password</Label><Input autoComplete="current-password" disabled={recoveryBusy} id="restore-decryption-password" onChange={event => setRecoveryPassword(event.target.value)} placeholder="Enter your backup password" type="password" value={recoveryPassword} /><p className="text-muted-foreground text-xs">Unlock your encrypted backup on this device.</p></div><Button disabled={recoveryBusy || !recoveryPassword} onClick={() => {
        onError(null);
        handleRecoveryRetry();
      }} type="button">Unlock backup</Button></>;
    $[56] = handleRecoveryRetry;
    $[57] = onError;
    $[58] = recoveryBusy;
    $[59] = recoveryPassword;
    $[60] = recoveryReady;
    $[61] = setRecoveryPassword;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== cancelRecovery || $[64] !== recoveryBusy) {
    t32 = recoveryBusy && <Button onClick={cancelRecovery} type="button" variant="outline">Cancel recovery</Button>;
    $[63] = cancelRecovery;
    $[64] = recoveryBusy;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  let t33;
  if ($[66] !== emailAuthPending || $[67] !== emailForOtp || $[68] !== handleEmailOtpSignIn || $[69] !== handleSendEmailOtp || $[70] !== lastMethod || $[71] !== onError || $[72] !== otpCode || $[73] !== otpSent || $[74] !== resetEmailOtp || $[75] !== setEmailForOtp || $[76] !== setOtpCode || $[77] !== setOtpSent || $[78] !== setShowEmailSignIn || $[79] !== showEmailSignIn) {
    t33 = showEmailSignIn ? <EmailOtpSection emailAuthPending={emailAuthPending} emailForOtp={emailForOtp} handleEmailOtpSignIn={handleEmailOtpSignIn} handleSendEmailOtp={() => {
      onError(null);
      handleSendEmailOtp();
    }} otpCode={otpCode} otpSent={otpSent} resetEmailOtp={resetEmailOtp} setEmailForOtp={setEmailForOtp} setOtpCode={setOtpCode} setOtpSent={setOtpSent} /> : <LastUsed active={lastMethod === "email"}><Button className={`w-full ${lastMethod === "email" ? LAST_USED_RING : ""}`} onClick={() => setShowEmailSignIn(true)} type="button" variant="outline"><Mail className="mr-2 h-4 w-4" />Sign in with Email</Button></LastUsed>;
    $[66] = emailAuthPending;
    $[67] = emailForOtp;
    $[68] = handleEmailOtpSignIn;
    $[69] = handleSendEmailOtp;
    $[70] = lastMethod;
    $[71] = onError;
    $[72] = otpCode;
    $[73] = otpSent;
    $[74] = resetEmailOtp;
    $[75] = setEmailForOtp;
    $[76] = setOtpCode;
    $[77] = setOtpSent;
    $[78] = setShowEmailSignIn;
    $[79] = showEmailSignIn;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div aria-hidden={true} className="flex items-center gap-3"><span className="h-px flex-1 bg-border" /><span className="text-muted-foreground text-xs">or</span><span className="h-px flex-1 bg-border" /></div>;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== socialSignInIsPending) {
    t35 = () => socialSignInIsPending;
    $[82] = socialSignInIsPending;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== socialProviderLoading || $[85] !== socialSignInIsPending) {
    t36 = provider_0 => socialSignInIsPending && socialProviderLoading === provider_0;
    $[84] = socialProviderLoading;
    $[85] = socialSignInIsPending;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== handleSocialSignIn || $[88] !== onError) {
    t37 = provider_1 => {
      onError(null);
      handleSocialSignIn(provider_1);
    };
    $[87] = handleSocialSignIn;
    $[88] = onError;
    $[89] = t37;
  } else {
    t37 = $[89];
  }
  let t38;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = ["github", "google", "apple", "twitter", "discord"];
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  let t39;
  if ($[91] !== lastProvider || $[92] !== t35 || $[93] !== t36 || $[94] !== t37) {
    t39 = <OAuthProviderButtons buttonClassName="w-full" buttonMode="icon" className="grid grid-cols-5 gap-2" getLabel={_temp3} highlightLabel="Last used on this device" highlightProvider={lastProvider} isDisabled={t35} isLoading={t36} onLink={t37} providers={t38} />;
    $[91] = lastProvider;
    $[92] = t35;
    $[93] = t36;
    $[94] = t37;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  let t40;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <p className="text-pretty text-center text-muted-foreground text-xs">{SOCIAL_SIGN_IN_NEW_ACCOUNT_NOTICE}</p>;
    $[96] = t40;
  } else {
    t40 = $[96];
  }
  let t41;
  if ($[97] !== t31 || $[98] !== t32 || $[99] !== t33 || $[100] !== t39) {
    t41 = <TabsContent className={TAB_PANEL} id="existing-sigma-sign-in-options" value="restore">{t31}{t32}{t33}{t34}{t39}{t40}</TabsContent>;
    $[97] = t31;
    $[98] = t32;
    $[99] = t33;
    $[100] = t39;
    $[101] = t41;
  } else {
    t41 = $[101];
  }
  let t42;
  if ($[102] !== handleTabChange || $[103] !== t30 || $[104] !== t41 || $[105] !== tab) {
    t42 = <Tabs onValueChange={handleTabChange} value={tab}>{t16}{t30}{t41}</Tabs>;
    $[102] = handleTabChange;
    $[103] = t30;
    $[104] = t41;
    $[105] = tab;
    $[106] = t42;
  } else {
    t42 = $[106];
  }
  let t43;
  if ($[107] !== t14 || $[108] !== t42) {
    t43 = <>{t14}{t42}</>;
    $[107] = t14;
    $[108] = t42;
    $[109] = t43;
  } else {
    t43 = $[109];
  }
  return t43;
}
function _temp3(provider) {
  switch (provider) {
    case "github":
      {
        return "Continue with GitHub";
      }
    case "google":
      {
        return "Continue with Google";
      }
    case "apple":
      {
        return "Continue with Apple";
      }
    case "discord":
      {
        return "Continue with Discord";
      }
    case "twitter":
      {
        return "Continue with X";
      }
    default:
      {
        return "Continue";
      }
  }
}
function _temp2() {
  document.getElementById("backup-import")?.click();
}
function _temp(next) {
  writeStored(STORAGE_KEYS.LOGIN_LAST_TAB, next);
}